import {
  SEARCH_PARAM_ORG_ID,
  SEARCH_PARAM_SITE_ID,
} from 'routes/(global_search_params)/-searchParams'
import { z } from 'zod'

export const SEARCH_PARAM_ALERT_ID = 'alertId'
export const SEARCH_PARAM_SENSOR_IDS = 'sensorIds'
export const SEARCH_PARAM_START = 'start'
export const SEARCH_PARAM_END = 'end'
const LEGACY_SEARCH_PARAM_MANUAL_ALERT_ID = 'manualAlertId'

const processDataOrgSiteSearchSchema = z.object({
  [SEARCH_PARAM_ORG_ID]: z.string().optional(),
  [SEARCH_PARAM_SITE_ID]: z.string(),
})

export type ProcessDataOrgSiteSearch = z.infer<
  typeof processDataOrgSiteSearchSchema
>

export const createProcessDataOrgSiteSearch = ({
  orgId,
  siteId,
}: ProcessDataOrgSiteSearch) => ({
  [SEARCH_PARAM_ORG_ID]: orgId,
  [SEARCH_PARAM_SITE_ID]: siteId,
})

const resolveAlertId = (
  alertId: string | undefined,
  manualAlertId: string | undefined
) => alertId ?? manualAlertId

/**
 * Normalize a bookmarked `start`/`end` pair.
 *
 * This is a live URL contract, so the rule is *drop, never throw*: an
 * unparseable or inconsistent range degrades to "no range in the URL", which
 * consumers (ChartViewer/AdHocChartViewer) already handle -- they require both
 * values (`if (!start || !end) return undefined`) and otherwise fall back to
 * their default range. Throwing would hard-404 a bookmarked link.
 *
 * We stay permissive about *format*: anything `Date.parse` accepts is kept, so
 * loose-but-valid bookmarks (`?start=2020-01-01`) keep working. We only reject
 * ranges that cannot describe real data:
 *   - either side unparseable (`start=garbage` -> `Invalid Date` downstream)
 *   - inverted or zero-width (`start=2030-01-01&end=2020-01-01`)
 *   - entirely in the future, which can never return points
 *
 * Values are normalized to ISO-8601 so downstream always gets a canonical,
 * re-parseable string. This preserves the instant -- it only fixes the spelling.
 */
const resolveTimeRange = (
  start: string | undefined,
  end: string | undefined,
  now: number = Date.now()
): { start: string | undefined; end: string | undefined } => {
  const dropped = { start: undefined, end: undefined }

  if (start === undefined || end === undefined) return dropped

  const startMs = Date.parse(start)
  const endMs = Date.parse(end)

  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return dropped
  // Inverted or zero-width. We can't tell a swap from a typo, so drop rather
  // than silently guess at an intent the user never expressed in the URL.
  if (startMs >= endMs) return dropped
  // A window that starts after now is unsatisfiable; an `end` past now is fine
  // (a range spanning "now" is exactly what a live-tail bookmark looks like).
  if (startMs >= now) return dropped

  return {
    start: new Date(startMs).toISOString(),
    end: new Date(endMs).toISOString(),
  }
}

export const processDataDashboardSearchSchema = z
  .object({
    ...processDataOrgSiteSearchSchema.shape,
    [SEARCH_PARAM_ALERT_ID]: z.string().optional(),
    [LEGACY_SEARCH_PARAM_MANUAL_ALERT_ID]: z.string().optional(),
  })
  .transform(({ orgId, siteId, alertId, manualAlertId }) => ({
    orgId,
    siteId,
    alertId: resolveAlertId(alertId, manualAlertId),
  }))

export type ProcessDataDashboardSearch = z.infer<
  typeof processDataDashboardSearchSchema
>

export const processDataChartSearchSchema = z
  .object({
    ...processDataOrgSiteSearchSchema.shape,
    [SEARCH_PARAM_ALERT_ID]: z.string().optional(),
    [LEGACY_SEARCH_PARAM_MANUAL_ALERT_ID]: z.string().optional(),
    [SEARCH_PARAM_SENSOR_IDS]: z.string().optional(),
    [SEARCH_PARAM_START]: z.string().optional(),
    [SEARCH_PARAM_END]: z.string().optional(),
  })
  .transform(
    ({ orgId, siteId, alertId, manualAlertId, sensorIds, start, end }) => ({
      orgId,
      siteId,
      alertId: resolveAlertId(alertId, manualAlertId),
      sensorIds,
      ...resolveTimeRange(start, end),
    })
  )

export type ProcessDataChartSearch = z.infer<
  typeof processDataChartSearchSchema
>

export const processDataViewSearchSchema = z
  .object({
    ...processDataOrgSiteSearchSchema.shape,
    [SEARCH_PARAM_ALERT_ID]: z.string().optional(),
    [LEGACY_SEARCH_PARAM_MANUAL_ALERT_ID]: z.string().optional(),
    [SEARCH_PARAM_SENSOR_IDS]: z.string().optional(),
    [SEARCH_PARAM_START]: z.string().optional(),
    [SEARCH_PARAM_END]: z.string().optional(),
  })
  .transform(
    ({ orgId, siteId, alertId, manualAlertId, sensorIds, start, end }) => ({
      orgId,
      siteId,
      alertId: resolveAlertId(alertId, manualAlertId),
      sensorIds,
      ...resolveTimeRange(start, end),
    })
  )

export type ProcessDataViewSearch = z.infer<typeof processDataViewSearchSchema>
